import { type IDBPDatabase, openDB } from 'idb';

import { AutoReconnectConnection } from '../../connection';
import type { SpaceType } from '../../utils/universal-id';
import { type DocStorageSchema, migrator } from './schema';
import track from '@affine/track';

export interface IDBConnectionOptions {
  flavour: string;
  type: SpaceType;
  id: string;
}

export class IDBConnection extends AutoReconnectConnection<{
  db: IDBPDatabase<DocStorageSchema>;
  channel: BroadcastChannel;
}> {
  readonly dbName = `${this.opts.flavour}:${this.opts.type}:${this.opts.id}`;

  override get shareId() {
    return `idb(${migrator.version}):${this.dbName}`;
  }

  constructor(private readonly opts: IDBConnectionOptions) {
    super();
  }

  override async doConnect() {
    try {
      // indexeddb will responsible for version control, so the db.version always match migrator.version
      const db = await openDB<DocStorageSchema>(this.dbName, migrator.version, {
        upgrade: migrator.migrate,
      });
      db.addEventListener('versionchange', this.handleVersionChange);

      return {
        db,
        channel: new BroadcastChannel('idb:' + this.dbName),
      };
    } catch (error) {
      // 监控数据库连接错误，特别是 InvalidStateError: Failed to execute 'transaction' on 'IDBDatabase': The database connection is closing.
      if (error instanceof Error) {
        track.doc.$.db.connectionError({
          type: error.name,
          error: error.message,
        });
      }
      throw error;
    }
  }

  override doDisconnect(db: {
    db: IDBPDatabase<DocStorageSchema>;
    channel: BroadcastChannel;
  }) {
    try {
      db.db.removeEventListener('versionchange', this.handleVersionChange);
      db.channel.close();
      db.db.close();
    } catch (error) {
      // 监控数据库断开连接时的错误
      if (error instanceof Error) {
        track.doc.$.db.connectionError({
          type: error.name,
          error: error.message,
        });
      }
      // 即使出错也继续执行，避免阻塞断开流程
    }
  }

  handleVersionChange = (e: IDBVersionChangeEvent) => {
    if (e.newVersion !== migrator.version) {
      const error = new Error(
        'Database version mismatch, expected ' +
          migrator.version +
          ' but got ' +
          e.newVersion
      );

      // 监控数据库版本不匹配错误
      track.doc.$.db.connectionError({
        type: 'VersionMismatchError',
        error: error.message,
      });

      this.error = error;
    }
  };
}
